/**
 * The staff console's API client.
 *
 * Deliberately separate from `src/api/client.ts` rather than an extension of it. That module is the
 * client-facing contract and is covered by tests that pin its failure behaviour; the console is a
 * different surface with a different cookie, and coupling them would mean a change made for staff
 * could alter what a customer sees.
 *
 * Every call is `credentials: 'same-origin'` and relies on the `ft_portal_staff` cookie, which is
 * http-only — the console cannot read it, and nothing here ever handles a token.
 */

import {
  API_ROUTES,
  type AdminAllowlistEntry,
  type AdminAuditEntry,
  type AdminConfirmMealLinkRequest,
  type AdminMealLinkQueue,
  type AdminMealLinkEditRequest,
  type AdminNoMatchMealLinkRequest,
  type AdminOperations,
  type AdminStaffMember,
  type MePayload,
  type StaffDogList,
  type StaffSessionInfo,
} from '@ft/shared'

import { reviveCareItems, type ApiResult } from '../api/client'

export type StaffResult<T> = { ok: true; data: T } | { ok: false; status: number; message: string }

async function request<T>(url: string, init?: RequestInit): Promise<StaffResult<T>> {
  let response: Response
  try {
    response = await fetch(url, {
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}) },
      ...init,
    })
  } catch {
    // A transport failure is not an empty result. Status 0 keeps "we could not reach the server"
    // distinguishable from "the server said no".
    return { ok: false, status: 0, message: 'We could not reach the portal. Check your connection.' }
  }

  if (response.status === 204) return { ok: true, data: undefined as T }

  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    body = null
  }

  if (!response.ok) {
    const message =
      body !== null && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : 'Something went wrong.'
    return { ok: false, status: response.status, message }
  }
  return { ok: true, data: body as T }
}

export const staffSignIn = (email: string): Promise<StaffResult<{ ok: true; message: string; devLink?: string }>> =>
  request(API_ROUTES.staffSignIn, { method: 'POST', body: JSON.stringify({ email }) })

export const staffConsume = (token: string): Promise<StaffResult<StaffSessionInfo>> =>
  request(API_ROUTES.staffConsume, { method: 'POST', body: JSON.stringify({ token }) })

export const staffSession = (): Promise<StaffResult<StaffSessionInfo>> => request(API_ROUTES.staffSession)

export const staffSignOut = (): Promise<StaffResult<void>> =>
  request(API_ROUTES.staffSignOut, { method: 'POST' })

export const staffDogs = (query: string): Promise<StaffResult<StaffDogList>> =>
  request(`${API_ROUTES.staffDogs}?q=${encodeURIComponent(query)}`)

// ── Super Administration Zone ───────────────────────────────────────────────────────────────────

/**
 * Sign in with email + PIN + password.
 *
 * All three go in one request. Checking them in stages would tell an attacker which one they had
 * already guessed, and the server deliberately returns the same message for every failure.
 */
export const adminPinSignIn = (
  email: string,
  pin: string,
  password: string
): Promise<StaffResult<StaffSessionInfo>> =>
  request(API_ROUTES.adminPinSignIn, {
    method: 'POST',
    body: JSON.stringify({ email, pin, password }),
  })

export const adminOperations = (): Promise<StaffResult<AdminOperations>> => request(API_ROUTES.adminOperations)

export const adminAudit = (limit: number): Promise<StaffResult<{ entries: AdminAuditEntry[] }>> =>
  request(`${API_ROUTES.adminAudit}?limit=${limit}`)

export const adminStaff = (): Promise<StaffResult<{ staff: AdminStaffMember[] }>> => request(API_ROUTES.adminStaff)

export const adminAllowlist = (): Promise<StaffResult<{ entries: AdminAllowlistEntry[] }>> =>
  request(API_ROUTES.adminAllowlist)

export const adminSync = (): Promise<StaffResult<{ ok: boolean; households: number; dogs: number; error: string | null }>> =>
  request(API_ROUTES.adminSync, { method: 'POST' })

export const adminMealLinks = (): Promise<StaffResult<AdminMealLinkQueue>> =>
  request(API_ROUTES.adminMealLinks)

export const adminConfirmMealLink = (body: AdminConfirmMealLinkRequest): Promise<StaffResult<void>> =>
  request(API_ROUTES.adminMealLinkConfirm, { method: 'POST', body: JSON.stringify(body) })

export const adminNoMatchMealLink = (body: AdminNoMatchMealLinkRequest): Promise<StaffResult<void>> =>
  request(API_ROUTES.adminMealLinkNoMatch, { method: 'POST', body: JSON.stringify(body) })

export const adminMealLinkEdit = (body: AdminMealLinkEditRequest): Promise<StaffResult<void>> =>
  request(API_ROUTES.adminMealLinkEdit, { method: 'POST', body: JSON.stringify(body) })

/** The two write surfaces: admit/remove a household, and add/deactivate/unlock a staff account. */
export const adminWrite = (
  path: 'allowlist' | 'staff',
  body: Record<string, string>
): Promise<StaffResult<void>> =>
  request(path === 'allowlist' ? API_ROUTES.adminAllowlist : API_ROUTES.adminStaff, {
    method: 'POST',
    body: JSON.stringify(body),
  })

/**
 * The household record, with its care dates turned back into real `Date`s.
 *
 * `CareItem.due` is typed `Date | null`, but JSON has no date type, so what actually arrives is a
 * string. The customer's client has always revived them (`reviveCareItems`); this one did not, and
 * the result was NOT an error — `fmtDay` threw on the first row, the render died, and because the
 * console had no error boundary the page simply went blank. Reported as "I click a dog and no
 * record appears".
 *
 * Reusing the client's reviver rather than writing a second one, so the two cannot disagree about
 * what a valid payload looks like. It returns null if any row is malformed, which is treated as a
 * bad response rather than silently dropping the schedule.
 */
export const staffHousehold = async (
  customerId: string,
  options?: { signal: AbortSignal }
): Promise<ApiResult<MePayload>> => {
  const result = await request<MePayload>(
    API_ROUTES.staffHousehold(customerId),
    options ? { signal: options.signal } : undefined
  )
  if (!result.ok) {
    // Adapted to the customer client's own result shape, because the record it feeds is rendered
    // by the customer's components — `PortalProvider` distinguishes a 401 from a transport failure
    // and must keep being able to.
    return {
      ok: false,
      status: result.status,
      error: { code: result.status === 0 ? 'network' : 'bad_response', message: result.message },
    }
  }

  const dogs = []
  for (const dog of result.data.dogs) {
    const care = reviveCareItems(dog.care)
    if (care === null) {
      return {
        ok: false,
        status: 502,
        error: {
          code: 'bad_response',
          message: 'That record came back in a shape we did not expect.',
        },
      }
    }
    dogs.push({ ...dog, care })
  }
  return { ok: true, data: { ...result.data, dogs } }
}
